import { useRef, useCallback, useState, useEffect } from 'react';

/**
 * useStreamingTTS Hook (Text-to-Speech)
 *
 * Abstracts TTS implementation - can be swapped between:
 * - Web Speech API (current - non-streaming)
 * - ElevenLabs (streaming)
 * - OpenAI TTS (streaming)
 * - Google Cloud TTS (streaming)
 *
 * Key features:
 * - Play audio chunks as they arrive (streaming)
 * - Immediate interruption support (stopTTS)
 * - Queue management for smooth playback
 */
export const useStreamingTTS = (options = {}) => {
  const {
    onSpeechStart,
    onSpeechEnd,
    onError,
    voice = null,
    rate = 1.1,
    pitch = 1.0
  } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const audioQueueRef = useRef([]);
  const currentUtteranceRef = useRef(null);
  const isPlayingRef = useRef(false);
  const pendingTextRef = useRef(null);

  // Load voices on mount (critical for Chrome)
  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      return;
    }

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
        console.log('✓ Speech synthesis voices loaded:', voices.length);
      }
    };

    // Load voices immediately
    loadVoices();

    // Chrome requires waiting for voiceschanged event
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  /**
   * Speak text immediately (non-streaming mode)
   */
  const speakText = useCallback((text) => {
    if (!('speechSynthesis' in window)) {
      console.error('❌ Speech synthesis not supported');
      onError?.('Speech synthesis not supported');
      return;
    }

    // If voices not loaded yet (Chrome), wait a bit and try again
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0 && !voicesLoaded) {
      console.log('⏳ Waiting for voices to load...');
      pendingTextRef.current = text;
      setTimeout(() => {
        if (pendingTextRef.current === text) {
          pendingTextRef.current = null;
          speakText(text);
        }
      }, 100);
      return;
    }

    console.log('🔊 Speaking:', text.substring(0, 50) + '...');

    // CRITICAL: Resume speech synthesis if paused (Chrome autoplay policy)
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      console.log('▶️  Resumed paused speech synthesis');
    }

    // CRITICAL: Cancel any pending speech to ensure fresh start
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = 1.0;

    // Try to use specified voice or find a female voice
    if (voices.length > 0) {
      const selectedVoice = voice || voices.find(v =>
        v.lang.startsWith('en') &&
        (v.name.includes('Female') || v.name.includes('Samantha') || v.name.includes('Karen'))
      );
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        console.log('🎵 Using voice:', selectedVoice.name);
      }
    }

    utterance.onstart = () => {
      console.log('▶️  TTS started - audio should be playing now');
      setIsSpeaking(true);
      isPlayingRef.current = true;
      onSpeechStart?.();
    };

    utterance.onend = () => {
      console.log('✅ TTS ended - audio finished playing');
      setIsSpeaking(false);
      isPlayingRef.current = false;
      currentUtteranceRef.current = null;
      onSpeechEnd?.();
    };

    utterance.onpause = () => {
      console.log('⏸️  TTS paused');
    };

    utterance.onresume = () => {
      console.log('▶️  TTS resumed');
    };

    utterance.onerror = (event) => {
      console.error('❌ TTS error:', event);
      setIsSpeaking(false);
      isPlayingRef.current = false;
      currentUtteranceRef.current = null;

      // Don't report 'canceled' as error (it's expected on interruption)
      if (event.error !== 'canceled') {
        onError?.(event.error);
      }
    };

    currentUtteranceRef.current = utterance;

    // Small delay to ensure speech synthesis is ready (Chrome workaround)
    setTimeout(() => {
      if (currentUtteranceRef.current === utterance) {
        window.speechSynthesis.speak(utterance);
      }
    }, 10);
  }, [rate, pitch, voice, onSpeechStart, onSpeechEnd, onError, voicesLoaded]);

  /**
   * Queue audio chunk for streaming playback
   * For real streaming TTS (ElevenLabs, OpenAI), this would:
   * 1. Receive audio chunk as ArrayBuffer/Blob
   * 2. Create AudioBuffer
   * 3. Play immediately or queue if busy
   */
  const queueAudioChunk = useCallback((audioChunk) => {
    audioQueueRef.current.push(audioChunk);
    // TODO: Implement actual streaming playback with Web Audio API
    // For now, this is a placeholder for future streaming implementation
  }, []);

  /**
   * Stop TTS immediately (interrupt)
   */
  const stopTTS = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      console.log('🛑 TTS stopped/interrupted');
    }
    setIsSpeaking(false);
    isPlayingRef.current = false;
    currentUtteranceRef.current = null;
    audioQueueRef.current = [];
  }, []);

  /**
   * Check if currently speaking
   */
  const checkIsSpeaking = useCallback(() => {
    return isPlayingRef.current || window.speechSynthesis?.speaking;
  }, []);

  return {
    speakText,
    queueAudioChunk,
    stopTTS,
    isSpeaking,
    checkIsSpeaking
  };
};

/**
 * Example integration with ElevenLabs streaming (for future):
 *
 * const audioContext = new AudioContext();
 * let sourceNode = null;
 *
 * const queueAudioChunk = async (chunk) => {
 *   const audioBuffer = await audioContext.decodeAudioData(chunk);
 *   sourceNode = audioContext.createBufferSource();
 *   sourceNode.buffer = audioBuffer;
 *   sourceNode.connect(audioContext.destination);
 *   sourceNode.onended = () => {
 *     // Notify chunk finished, play next in queue
 *   };
 *   sourceNode.start(0);
 * };
 *
 * const stopTTS = () => {
 *   if (sourceNode) {
 *     sourceNode.stop();
 *     sourceNode.disconnect();
 *   }
 * };
 */
